import { FormEvent, useEffect, useState } from "react";

import { TaskConstants } from "../../../../../constants/workflow/TaskConstants";
import { approveOrRejectDeal } from "../../../../../service/fund";
import { searchWorkflowTasks } from "../../../../../service/query";
import { ServiceError, Status } from "../../../../../service/Shared";
import { Toast } from "../../../../../widget";
import { CloseDealStatus } from "./models";

interface UseCloseDealModalProps {
  show: boolean;
  closeModal: () => void;
  dealUuid?: string;
  workflowUuid?: string;
  rowVersion: number;
  refreshDeal: () => void;
  applyCloseDealUpdate: (status: CloseDealStatus, rowVersion?: number) => void;
}

interface UseCloseDealModalReturnData {
  status?: CloseDealStatus;
  dealLostReason: string;
  isRejected: boolean;
  isSubmitDisabled: boolean;
  isHandleSubmitLoading: boolean;
  hasOutstandingTasks: boolean;
  errors: ServiceError[] | undefined;
  setStatus: (status?: CloseDealStatus) => void;
  setDealLostReason: (dealLostReason: string) => void;
  handleCloseModal: () => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
}

export const useCloseDealModal = ({
  show,
  closeModal,
  dealUuid,
  workflowUuid,
  rowVersion,
  refreshDeal,
  applyCloseDealUpdate,
}: UseCloseDealModalProps): UseCloseDealModalReturnData => {
  const [status, setStatus] = useState<CloseDealStatus>();
  const [dealLostReason, setDealLostReason] = useState<string>("");
  const [hasOutstandingTasks, setHasOutstandingTasks] = useState(false);
  const [errors, setErrors] = useState<ServiceError[]>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const isRejected = status === "Rejected";
  const isSubmitDisabled = !status || (isRejected && !dealLostReason);

  const handleCloseModal = (): void => {
    setErrors([]);
    closeModal();
  };

  useEffect(() => {
    let isActive = true;

    if (!show || !workflowUuid) {
      setHasOutstandingTasks(false);
      return () => {
        isActive = false;
      };
    }

    const fetchOutstandingTasks = async (): Promise<void> => {
      const response = await searchWorkflowTasks({
        paging: {
          limit: 1,
          beforeCursor: null,
          afterCursor: null,
        },
        filter: {
          results: {
            status: {
              operator: "neq",
              value: TaskConstants.TASK_STATUS_COMPLETE,
            },
            workflowUuid: {
              operator: "eq",
              value: workflowUuid,
            },
          },
        },
      });

      if (!isActive) {
        return;
      }

      setHasOutstandingTasks((response.data?.paging?.total ?? response.data?.results?.length ?? 0) > 0);
    };

    fetchOutstandingTasks();

    return () => {
      isActive = false;
    };
  }, [show, workflowUuid]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (!dealUuid || !status) {
      return;
    }

    setIsHandleSubmitLoading(true);
    setErrors([]);

    const response = await approveOrRejectDeal({
      dealLostReason: isRejected ? dealLostReason : null,
      dealUuid,
      rowVersion,
      status,
    });

    if (response.status === Status.Success) {
      Toast.success({ message: `Deal ${status.toLowerCase()} successfully` });
      applyCloseDealUpdate(status, response.data?.rowVersion);
      handleCloseModal();
      refreshDeal();
      setIsHandleSubmitLoading(false);
      return;
    }

    setErrors(response.errors);
    setIsHandleSubmitLoading(false);
  };

  return {
    status,
    dealLostReason,
    isRejected,
    isSubmitDisabled,
    isHandleSubmitLoading,
    hasOutstandingTasks,
    errors,
    setStatus,
    setDealLostReason,
    handleCloseModal,
    handleSubmit,
  };
};
